import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Info from './Info';
import { useAdminLanguage } from './AdminI18n';
import { adminFetch } from './adminApi';

function ExaminerAdmin() {
  const { t } = useAdminLanguage();
  const [examiners, setExaminers] = useState([]);
  const [formData, setFormData] = useState({ nom: '', prenom: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchExaminers();
  }, []);

  const fetchExaminers = async () => {
    try {
      const response = await adminFetch('/api/examiners');
      const data = await response.json();
      setExaminers(data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des médecins légistes');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.nom.trim() || !formData.prenom.trim()) {
        setError('Tous les champs sont obligatoires');
        return;
      }

      setLoading(true);
      setError('');
      setSuccess('');

      const dataToSend = { ...formData, nom: formData.nom.toUpperCase() };

      if (isEditing) {
        await adminFetch(`/api/examiners/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataToSend),
        });
        setSuccess('Médecin légiste mis à jour avec succès');
      } else {
        await adminFetch('/api/examiners', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataToSend),
        });
        setSuccess('Médecin légiste ajouté avec succès');
      }

      setFormData({ nom: '', prenom: '' });
      setIsEditing(false);
      setEditingId(null);
      fetchExaminers();
    } catch (err) {
      setError('Erreur lors de l\'enregistrement du médecin légiste');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (examiner) => {
    setIsEditing(true);
    setEditingId(examiner.id);
    setFormData({ nom: examiner.nom, prenom: examiner.prenom });
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await adminFetch(`/api/examiners/${id}`, {
        method: 'DELETE',
      });
      setSuccess('Médecin légiste supprimé avec succès');
      fetchExaminers();
    } catch (err) {
      setError('Erreur lors de la suppression du médecin légiste');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des médecins légistes')}
          </Typography>

          <Info type="examiner" />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(error)}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t(success)}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t(isEditing ? 'Modifier le médecin légiste' : 'Ajouter un médecin légiste')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="nom"
                  label={t('Nom')}
                  autoComplete="off"
                  value={formData.nom}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="prenom"
                  label={t('Prénom')}
                  autoComplete="off"
                  value={formData.prenom}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? t('Enregistrement...') : t(isEditing ? 'Modifier' : 'Ajouter')}
                </Button>
                {isEditing && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setIsEditing(false);
                      setEditingId(null);
                      setFormData({ nom: '', prenom: '' });
                    }}
                    sx={{ ml: 2 }}
                  >
                    {t('Annuler')}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              {t('Liste des médecins légistes')}
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('Nom')}</TableCell>
                    <TableCell>{t('Prénom')}</TableCell>
                    <TableCell>{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {examiners.map((examiner) => (
                    <TableRow key={examiner.id}>
                      <TableCell>{examiner.nom}</TableCell>
                      <TableCell>{examiner.prenom}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(examiner)}
                            color="primary"
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(examiner.id)}
                            color="error"
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Fade>
    </Container>
  );
}

export default ExaminerAdmin;
